/**
 * SafeResponsiveContainer — Recharts wrapper that defers mount until
 * the parent has non-zero pixel dimensions.
 *
 * Recharts' ResponsiveContainer will enter a -1×-1 measurement loop
 * when mounted inside a parent that hasn't laid out yet (display:none,
 * conditional render flickering, parent grid still computing). This
 * wrapper sits ABOVE the Recharts container and uses ResizeObserver
 * to confirm the parent has measured before rendering anything that
 * triggers Recharts' own measurement pass.
 *
 * Drop-in replacement for `<ResponsiveContainer>`. Identical prop
 * surface — width / height / minWidth / minHeight / aspect / debounce
 * / id / className / children — forwarded verbatim.
 *
 * Optional instrumentation: set `window.__DEEPATOMIC_TRACE_CHARTS__`
 * to true in the prod console and reload. Each chart mount logs its
 * call stack so the source location of unexpected mounts (e.g. a
 * chart on the Dashboard view despite engineView==='dashboard') is
 * attributable. Off by default.
 */
import React from 'react';
import { ResponsiveContainer } from 'recharts';

type RechartsResponsiveProps = React.ComponentProps<typeof ResponsiveContainer>;

interface Props extends RechartsResponsiveProps {
  /** Optional explicit name surfaced in the chart-mount tracer. */
  traceName?: string;
  /**
   * RCD-2 — optional override for the minimum observed width before the
   * inner Recharts container mounts. Defaults to {@link READY_THRESHOLD_PX}.
   * Callers normally don't need this; exposed to let above-the-fold charts
   * with reliable layout opt into a tighter gate.
   */
  minVisibleWidth?: number;
  /**
   * RCD-2 — optional override for the minimum observed height before the
   * inner Recharts container mounts. Defaults to {@link READY_THRESHOLD_PX}.
   * Charts inside accordion rows (Site Evidence) can pass `1` so the gate
   * fires as soon as the row begins to expand rather than waiting for the
   * 8px floor.
   */
  minVisibleHeight?: number;
  /**
   * RRQ — print/paginated escape hatch. When the chart lives inside the
   * paginated deck, the /print export, or the offscreen pagination source,
   * the wrapper can be CLIPPED by an ancestor (e.g. the pagination host) so
   * its `getBoundingClientRect()` reads ~0 even though the reserved CSS
   * height is real. The measurement gate then never releases and the chart
   * renders LEGEND-ONLY (the recharts SVG never mounts).
   *
   * `eager` forces the inner ResponsiveContainer to mount IMMEDIATELY at the
   * reserved height, bypassing the measurement gate, so the chart always
   * paints in export/paged/print contexts. Off by default ⇒ the interactive
   * viewer keeps the measure-then-mount guard (recharts -1×-1 protection) and
   * every existing call site is byte-stable.
   */
  eager?: boolean;
  /**
   * RRQ — last-resort timeout (ms). When NOT eager and the wrapper never
   * lands a usable measurement (clipped ancestor / detached clone), mount the
   * inner container anyway after this delay PROVIDED a concrete reserved
   * height is known, so a chart can never stay legend-only forever. Set to 0
   * to disable. Defaults to {@link READY_FALLBACK_TIMEOUT_MS}.
   */
  readyFallbackMs?: number;
}

/**
 * Pixel threshold for "ready". Recharts breaks at zero or negative
 * dimensions; anything ≥ 1×1 is enough to keep the inner container's
 * measurement pass from flailing. We use 8×8 to absorb sub-pixel
 * rounding during layout transitions.
 */
const READY_THRESHOLD_PX = 8;

/**
 * RRQ — default ceiling for the measurement gate. If the wrapper has not
 * landed a usable measurement within this window (clipped ancestor in the
 * paged/print flow, detached clone, etc.) we mount the inner container at
 * the reserved height anyway. Comfortably longer than a normal layout
 * settle, so the interactive viewer always mounts via the measurement path
 * first and this only fires on a genuinely starved measurement.
 */
const READY_FALLBACK_TIMEOUT_MS = 1200;

/** Does the recharts `height` prop carry a concrete, reservable height
 *  (a positive number, or a fixed CSS px/rem/etc. — NOT '100%')? Only then
 *  can the timeout fallback mount a usefully-sized chart. */
function hasReservedHeight(height: unknown): boolean {
  if (typeof height === 'number') return height > 0;
  if (typeof height === 'string') {
    const h = height.trim();
    if (h === '' || h === '100%' || h === 'auto') return false;
    return /\d/.test(h); // e.g. '240px', '18rem', 'calc(...)'
  }
  return false;
}

export const SafeResponsiveContainer: React.FC<Props> = ({
  traceName,
  children,
  minVisibleWidth = READY_THRESHOLD_PX,
  minVisibleHeight = READY_THRESHOLD_PX,
  eager = false,
  readyFallbackMs = READY_FALLBACK_TIMEOUT_MS,
  ...rechartsProps
}) => {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  // `eager` mounts immediately (print/paged escape hatch) — the wrapper's
  // reserved CSS height is the chart's measurable box, so recharts gets a
  // real height even when an ancestor clips the live rect to ~0.
  const [ready, setReady] = React.useState(eager);

  // `eager` can flip false→true AFTER mount (ChartFrame resolves its
  // export/print context in a layout effect, one tick post-mount). Promote to
  // ready when that happens so the inner container mounts at the reserved box.
  React.useEffect(() => {
    if (eager) setReady(true);
  }, [eager]);

  // Stack-capture instrumentation. Toggle via the global flag in the
  // prod console; off by default so production renders stay quiet.
  // The stack is captured once per mount — re-renders don't re-log.
  const tracedRef = React.useRef(false);
  React.useEffect(() => {
    if (tracedRef.current) return;
    if (typeof window === 'undefined') return;
    const flag = (window as unknown as { __DEEPATOMIC_TRACE_CHARTS__?: boolean }).__DEEPATOMIC_TRACE_CHARTS__;
    if (!flag) return;
    tracedRef.current = true;
    const stackLines = new Error().stack?.split('\n').slice(2, 8).join(' | ') ?? '';
    // eslint-disable-next-line no-console
    console.log('[chart-mount]', traceName ?? 'unnamed', stackLines);
  });

  React.useEffect(() => {
    // `eager` already mounted via the initial state — no measurement gate.
    if (eager) return;
    const el = wrapperRef.current;
    if (!el) return;

    // Initial sync check — covers the common case where the parent has
    // already laid out by mount time (e.g. report view fully visible).
    const check = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width >= minVisibleWidth && rect.height >= minVisibleHeight) {
        setReady(true);
        return true;
      }
      return false;
    };
    if (check()) return;

    // Otherwise watch the wrapper until it lands a usable measurement.
    // ResizeObserver fires after layout; we set state once and tear
    // down the observer to keep the mount cost flat.
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            const { width, height } = entry.contentRect;
            if (width >= minVisibleWidth && height >= minVisibleHeight) {
              setReady(true);
              observer?.disconnect();
              if (fallbackTimer) clearTimeout(fallbackTimer);
            }
          })
        : null;
    observer?.observe(el);

    // RRQ — last-resort timeout. In the paginated deck / /print export the
    // wrapper's ancestor host can be CLIPPED to ~0 while pagedjs measures +
    // rasterises, so neither the sync check nor the ResizeObserver ever
    // reports a usable size and the chart stays legend-only. When a concrete
    // reserved height is known (ChartFrame seeds one), mount the inner
    // container anyway after the ceiling so the SVG paints at its reserved
    // box. Guarded on a positive timeout AND a reservable height, so the
    // interactive viewer's measure-first behaviour is unchanged.
    if (readyFallbackMs > 0 && hasReservedHeight(rechartsProps.height)) {
      fallbackTimer = setTimeout(() => {
        setReady(true);
        observer?.disconnect();
      }, readyFallbackMs);
    }

    return () => {
      observer?.disconnect();
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, [minVisibleWidth, minVisibleHeight, eager, readyFallbackMs, rechartsProps.height]);

  // Resolve a wrapper height for the spacer slot. Recharts accepts
  // string ('100%') or number — we pass through string sentinels so
  // the parent can still drive layout, and convert numbers to px so
  // the spacer reserves the right space pre-mount.
  const wrapperStyle: React.CSSProperties = {
    width: '100%',
    height: typeof rechartsProps.height === 'number' ? `${rechartsProps.height}px` : (rechartsProps.height as string | undefined) ?? '100%',
    minWidth: rechartsProps.minWidth as number | string | undefined,
    minHeight: rechartsProps.minHeight as number | string | undefined,
  };

  return (
    <div ref={wrapperRef} style={wrapperStyle} data-testid="safe-responsive-container" data-ready={ready ? 'true' : 'false'}>
      {ready ? (
        <ResponsiveContainer {...rechartsProps}>
          {children as React.ReactElement}
        </ResponsiveContainer>
      ) : null}
    </div>
  );
};
